<script setup lang="ts">
import { computed } from "vue";
import dayjs from "dayjs";
import Modal from "@/ui/components/modal.vue";
import { t } from "@/plugins/i18n";
import { useExport } from "@/modules/exports/composables/useExport";

const { exportUuid } = defineProps<{
	exportUuid?: string;
}>();

const isDialogVisible = defineModel<boolean>({ default: false });

const { exportItem, isLoading } = useExport(
	() => exportUuid,
	() => isDialogVisible.value
);

const formatDate = (value?: string | null) =>
	value ? dayjs(value).format("DD MMM YYYY HH:mm") : "-";

const formatFileSize = (bytes?: number | null) => {
	if (!bytes) return "-";
	const units = ["B", "KB", "MB", "GB"];
	let size = bytes;
	let unit = 0;
	while (size >= 1024 && unit < units.length - 1) {
		size /= 1024;
		unit++;
	}
	return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const details = computed(() => {
	if (!exportItem.value) {
		return [];
	}

	return [
		{ label: "File name", value: exportItem.value.file_name ?? "-" },
		{
			label: "Type",
			value: t(`scheduler.export_types.${exportItem.value.export_type_name}`),
		},
		{ label: "Status", value: exportItem.value.status },
		{ label: "File size", value: formatFileSize(exportItem.value.file_size) },
		{
			label: "Scheduled",
			value: exportItem.value.is_scheduled ? "Yes" : "No",
		},
		{ label: "Started at", value: formatDate(exportItem.value.started_at) },
		{ label: "Finished at", value: formatDate(exportItem.value.finished_at) },
	];
});
</script>

<template>
	<Modal
		v-model:visible="isDialogVisible"
		title="Export Details"
		icon="fa-light fa-circle-info"
		hide-actions
	>
		<div v-if="isLoading" class="d-flex justify-center py-8">
			<v-progress-circular indeterminate color="primary" />
		</div>

		<template v-else-if="exportItem">
			<dl class="details-grid">
				<template v-for="detail in details" :key="detail.label">
					<dt>{{ detail.label }}</dt>
					<dd>{{ detail.value }}</dd>
				</template>
			</dl>

			<v-divider class="my-4" />

			<h4 class="mb-2">Downloads ({{ exportItem.downloads.length }})</h4>
			<perfect-scrollbar
				class="downloads-scroll"
				:options="{ suppressScrollX: true }"
			>
				<v-list
					v-if="exportItem.downloads.length"
					density="compact"
					lines="two"
				>
					<v-list-item
						v-for="download in exportItem.downloads"
						:key="download.id"
						:title="download.user?.name ?? download.ip_address"
						:subtitle="formatDate(download.created_at)"
					>
						<template #prepend>
							<i class="fa-light fa-download mr-3" />
						</template>
					</v-list-item>
				</v-list>
				<div v-else class="text-medium-emphasis text-center py-6">
					No downloads yet
				</div>
			</perfect-scrollbar>
		</template>

		<div v-else class="text-medium-emphasis text-center py-8">
			Export not found
		</div>
	</Modal>
</template>

<style scoped lang="scss">
.details-grid {
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: 1.5rem;
	row-gap: 0.5rem;

	dt {
		font-weight: 600;
		color: rgba(var(--v-theme-on-surface), 0.7);
	}

	dd {
		margin: 0;
	}
}

.downloads-scroll {
	height: 200px; // fixed height so the area never resizes with the list
	border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	border-radius: 4px;
}
</style>
